import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { UserContext } from '../../UserContext';
import { useContext } from 'react';

/**
 * Custom hook for managing common activity data and state
 * @param {string} activityId - Activity ID from URL params
 * @returns {Object} Activity data and state management functions
 */
export const useActivityData = (activityId) => {
  const { userInfo } = useContext(UserContext);
  
  // Common state variables
  const [actualQuestions, setActualQuestions] = useState([]);
  const [currQuestion, setCurrQuestion] = useState(null);
  const [imageData, setImageData] = useState(null);
  const [count, setCount] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [marksPerQuestion, setMarksPerQuestion] = useState(0);
  const [totalMarks, setTotalMarks] = useState(0);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [gainedMarks, setGainedMarks] = useState(0);
  const [items, setItems] = useState([]);
  const [isFinished, setIsFinished] = useState(false);
  const [studentId, setStudentId] = useState(null);

  // Refs for timer and progress
  const countRef = useRef(0);
  const totalQuestionsRef = useRef(0);
  const progressIncrement = useRef(0);
  const questionNumberRef = useRef(0);
  const progressCount = useRef(0);
  const gainedMarksRef = useRef(0);
  const totalTimeTakenOfAllQuestions = useRef(0);
  const totalTimeOfAllQuestions = useRef(0);
  const completeActivityRef = useRef(0);

  // Fetch student ID from user info
  useEffect(() => {
    const fetchStudentId = async () => {
      try {
        const response = await axios.post(
          `${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/getStudentIDfromUser`,
          {
            UserId: userInfo?.User_id,
          }
        );
        const studentId = response.data[0]?.Student_id || null;
        setStudentId(studentId);
      } catch (error) {
        console.error('Error fetching student ID:', error);
      }
    };

    if (userInfo?.User_id) {
      fetchStudentId();
    }
  }, [userInfo?.User_id]);

  // Fetch activity questions
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await axios.get(
          `${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/getActivityQuestions/${activityId}`
        );
        
        setActualQuestions(response.data);
        setCurrQuestion(response.data[0]);
        setCount(response.data[0]?.timealloted || 0);
        countRef.current = response.data[0]?.timealloted || 0;
        setMarksPerQuestion(response.data[0]?.marks || 0);

        // Calculate totals
        let total_marks = 0;
        let total_questions = 0;
        response.data.forEach(data => {
          total_marks += data.marks || 0;
          total_questions++;
        });

        setTotalMarks(total_marks);
        setTotalQuestions(total_questions);
        totalQuestionsRef.current = total_questions;
        progressIncrement.current = total_questions ? (100 / total_questions) * 1.3 : 0;

        // Fetch image if available
        if (response.data[0]?.questionpicture) {
          const { fetchActivitiesImage } = await import('../fetchActivitiesImage');
          fetchActivitiesImage(setImageData, response.data[0].questionpicture);
        }
      } catch (error) {
        console.error('Error fetching questions:', error);
      }
    };

    if (activityId) {
      fetchQuestions();
    }
  }, [activityId]);

  return {
    // State
    actualQuestions,
    currQuestion,
    imageData,
    count,
    totalQuestions,
    marksPerQuestion,
    totalMarks,
    questionNumber,
    gainedMarks,
    items,
    isFinished,
    studentId,
    
    // Setters
    setActualQuestions,
    setCurrQuestion,
    setImageData,
    setCount,
    setTotalQuestions,
    setMarksPerQuestion,
    setTotalMarks,
    setQuestionNumber,
    setGainedMarks,
    setItems,
    setIsFinished,
    
    // Refs
    countRef,
    totalQuestionsRef,
    progressIncrement,
    questionNumberRef,
    progressCount,
    gainedMarksRef,
    totalTimeTakenOfAllQuestions,
    totalTimeOfAllQuestions,
    completeActivityRef
  };
};
